import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const escalationAdapter = createEntityAdapter({
  selectId: (escalation) => escalation._id,
  sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
});

// Async Thunks
export const fetchEscalationData = createAsyncThunk(
  "escalation/fetch",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await request({
        endpoint: "/admin/support/getEscalations",
        method: "GET",
        params: { startDate, endDate },
      });
      if (!response.success || !Array.isArray(response.data)) {
        return rejectWithValue(
          response.message || "Failed to fetch escalations"
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch escalations");
    }
  }
);

export const resolveEscalation = createAsyncThunk(
  "escalation/resolve",
  async ({ escalationData, id }, { rejectWithValue }) => {
    try {
      const response = await request({
        endpoint: `/admin/support/resolveEscalation`,
        method: "PUT",
        body: escalationData,
        params: { id },
      });

      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error.message || "Failed to resolve escalation"
        );
      }

      return response.data;
    } catch ({ error }) {
      return rejectWithValue(error.message || "Failed to resolve escalation");
    }
  }
);
export const bulkUploadEscalation = createAsyncThunk(
  "escalation/bulkUpload",
  async (file, { rejectWithValue }) => {
    try {
      const response = await request({
        endpoint: `/admin/support/bulkUpdate`,
        method: "POST",
        body: file,
      });
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error.message || "Failed to bulk upload escalation"
        );
      }
      return response.data;
    } catch ({ error }) {
      return rejectWithValue(
        error.message || "Failed to bulk upload escalation"
      );
    }
  }
);

export const replyEscalation = createAsyncThunk(
  "escalation/reply",
  async ({ data, escalationId }, { rejectWithValue }) => {
    try {
      // Construct FormData for file upload
      const response = await request({
        endpoint: "/admin/support/replyEscalation",
        method: "POST",
        body: data,
        params: { escalationId },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Reply Escalation API Response:", response);

      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error.message || "Failed to Reply escalation."
        );
      }

      return response.data; // Return the escalation data on success
    } catch ({ error }) {
      console.error("Error Reply escalation:", error.message || error);
      return rejectWithValue(
        error.message || error || "Failed to Reply escalation."
      );
    }
  }
);

// Initial state
const initialState = escalationAdapter.getInitialState({
  loading: false,
  error: null,
});

// Slice
const escalationSlice = createSlice({
  name: "escalation",
  initialState,
  reducers: {
    clearEscalations: (state) => {
      escalationAdapter.removeAll(state);
      state.loading = false;
      state.error = null;
    },
    addAllEscalationsIO: (state, action) => {
      escalationAdapter.setAll(state, action.payload);
    },
    addEscalationOneIO: (state, action) => {
      escalationAdapter.addOne(state, action.payload);
    },
    updateEscalationOneIO: (state, action) => {
      escalationAdapter.updateOne(state, action.payload);
    },
    deleteEscalationOneIO: (state, action) => {
      escalationAdapter.removeOne(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Escalations
      .addCase(fetchEscalationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEscalationData.fulfilled, (state, action) => {
        escalationAdapter.setAll(state, action.payload);
        state.loading = false;
      })
      .addCase(fetchEscalationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resolve Escalation
      .addCase(resolveEscalation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resolveEscalation.fulfilled, (state, action) => {
        escalationAdapter.upsertOne(state, action.payload);
        state.loading = false;
      })
      .addCase(resolveEscalation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk Upload Escalation
      .addCase(bulkUploadEscalation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUploadEscalation.fulfilled, (state, action) => {
        escalationAdapter.addMany(state, action.payload);
        state.loading = false;
      })
      .addCase(bulkUploadEscalation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reply Escalation
      .addCase(replyEscalation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replyEscalation.fulfilled, (state, action) => {
        // Check if we have the full escalation object in the response
        escalationAdapter.upsertOne(state, action.payload.escalation);
        state.loading = false;
      })
      .addCase(replyEscalation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Update selectors export
export const {
  selectAll: selectAllEscalations,
  selectById: selectEscalationById,
  selectIds: selectEscalationIds,
} = escalationAdapter.getSelectors((state) => state.escalation);

// Update actions export
export const {
  clearEscalations,
  addAllEscalationsIO,
  addEscalationOneIO,
  updateEscalationOneIO,
  deleteEscalationOneIO,
} = escalationSlice.actions;
export default escalationSlice.reducer;
